import axios, { AxiosRequestConfig } from 'axios';

import { LensMapper } from '~src/lens/models/mappers/LensMapper';
import { ProjectMapper } from '~src/projects/models/mappers/ProjectMapper';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { ViewMapper } from '~src/views/mappers/ViewMapper';

import { SessionSiteSelectors, SessionUserSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { CollectionMapper } from '../../Collections/mappers/CollectionMapper';
import { DataRoleMapper } from '../../dataRole/models/mappers/DataRoleMapper';
import { DatasourceMapper } from '../../datasource/models/mappers/DatasourceMapper';
import { FlowMapper } from '../../flow/models/mappers/FlowMapper';
import { MetricMapper } from '../../metrics/models/mappers/MetricMapper';
import { PublishedConnectionMapper } from '../../publishedConnection/models/mappers/PublishedConnectionMapper';
import { PublishedConnectionTableMapper } from '../../publishedConnectionTable/models/mappers/PublishedConnectionTableMapper';
import { WorkbookMapper } from '../../workbook/models/mappers/WorkbookMapper';
import { OrderKeys } from '../models/OrderKeys';

// Change endpoint to absolute path if you want to run a mixed local scenario
// e.g. local - 'http://localhost:8080/content-exploration/content'
//      kube -  'content-exploration/content'
// jhallmann-20190423: need a story for dev/production configuration
const CRASS_URI: string = 'content-exploration/content';

export class Search {
  private searchExpression: String;
  private filterExpression: String;
  private sortExpression: String;
  private pageIndex = 0;
  private pageSize = 10;

  public search(expression: String): Search {
    this.searchExpression = expression;
    return this;
  }

  public filter(expression: String): Search {
    this.filterExpression = expression;
    return this;
  }

  public sort(expression: String): Search {
    this.sortExpression = expression;
    return this;
  }

  public withOrder(clauses: Array<IOrderClause>): Search {
    if (clauses && clauses.length > 0) {
      let expressions: Array<string> = [];
      for (let clause of clauses) {
        // TODO: Add support for explicit relevance sort.
        if (clause.field === OrderKeys.relevance) {
          continue;
        }

        expressions.push(`${clause.field} ${clause.ascending ? 'asc' : 'desc'}`);
      }
      this.sortExpression = expressions.join(',');
    } else {
      this.sortExpression = '';
    }
    return this;
  }

  public page(pageIndex: number): Search {
    this.pageIndex = pageIndex;
    return this;
  }

  public limit(pageSize: number): Search {
    this.pageSize = pageSize;
    return this;
  }

  public getCount(): Promise<number> {
    return this.getResults().then(result => result.total);
  }

  public getResults(): Promise<SearchResult> {
    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    const siteId = SessionSiteSelectors.siteId(getAppStore().getState());

    const params = new Array<String>();
    if (this.searchExpression) {
      params.push('search=' + this.searchExpression);
    }
    if (this.filterExpression) {
      params.push('filter=' + this.filterExpression);
    }
    if (this.sortExpression) {
      params.push('sort=' + this.sortExpression);
    }
    params.push('page=' + this.pageIndex);
    params.push('limit=' + this.pageSize);
    params.push(`user=${userId}`);
    params.push(`site=${siteId}`);
    params.push(`admin=true`);


    const url = CRASS_URI + '?' + params.join('&');
    const requestConfig: AxiosRequestConfig = {
      method: 'GET',
      url
    };

    return axios.request(requestConfig).then((response: any) => {
      // TODO: Map crass data into required fields for vizportalclient.
      return {
        ...response.data,
        items: response.data.items.map((item: any) => mapContentForContentType(item.content.type, item.content))
      };
    });
  }
}

export type SearchResult = {
  pageIndex: number;
  size: number;
  total: number;
  items: ReadonlyArray<any>;
};

function mapDefault(contentType: string, content: any) {
  return {
    ...content,
    id: content.syncId,
    contentType: contentType,
    name: content.title,
    tags: [],
    owner: {
      id: 0,
      displayName: content.ownerName || '',
      username: content.ownerUserName || '',
      domainName: '',
      isHiddenInternalUser: false
    }
  };
}

// These mappers have some tech debt associated with parent Projects and CRASS versions. See TFS 1235528
function mapContentForContentType(contentType: string, content: any) {
  switch (contentType) {
    case 'workbook':
      return WorkbookMapper.mapFromCRASS(content);
    case 'unifieddatasource':
      return DatasourceMapper.mapFromCRASS(content);
    case 'view':
      return ViewMapper.mapFromCRASS(content);
    case 'project':
      return ProjectMapper.mapFromCRASS(content);
    case 'flow':
      return FlowMapper.mapFromCRASS(content);
    case 'metric':
      return MetricMapper.mapFromCRASS(content);
    case 'collection':
      return CollectionMapper.mapFromCRASS(content);
    case 'virtualconnection':
      return PublishedConnectionMapper.mapFromCRASS(content);
    case 'virtualconnectiontable':
        return PublishedConnectionTableMapper.mapFromCRASS(content);
    case 'datarole':
      return DataRoleMapper.mapFromCRASS(content);
    case 'lens':
      return LensMapper.mapFromCRASS(content);
    default:
      return mapDefault(contentType, content);
  }
}

export function mapContentFromCrass(item: any): any {
  return mapContentForContentType(item.content.contentType, item.content);
}
